import { AttackPattern, PatternState } from '../AttackPattern.js';

/**
 * Swell (The Swell, Act II) — the deep gives back exactly what you give it.
 * At the moment the move telegraphs, it reads how hard the boss has been hit
 * lately (`boss.recentDamage`, a decaying tally maintained by the Boss entity)
 * and scales the volley it's about to fire to match: burst the boss down and
 * the answer spikes; deal measured, steady damage and it stays survivable.
 *
 * The signature is that mirror, not the volley itself (a plain radial). The
 * telegraph previews the incoming size (a ring that grows with the swell) so
 * the cause -> effect is legible: "I just dumped everything, and now look what's
 * coming." The lesson the fight teaches is to play against instinct — not
 * louder, steadier.
 */
export class Swell extends AttackPattern {
  constructor({ minPhase = 0, config }) {
    super({ name: 'Swell', telegraphDuration: 1.1, activeDuration: 0.5, minPhase });
    this.config = config;
    this._swell = 1;
  }

  // Computed at telegraph time so the preview and the volley agree, and so a
  // burst lands on the NEXT swell rather than the one already in flight.
  onBegin(boss, _player) {
    const { softThreshold, maxSwell } = this.config;
    const loudness = Math.min(1, (boss.recentDamage ?? 0) / softThreshold);
    this._swell = 1 + loudness * (maxSwell - 1);
    if (boss.phase >= (this.config.crescendoPhase ?? 99)) this._swell = maxSwell;
  }

  onActiveStart(boss, _player, pool) {
    const { baseCount, baseSpeed, damage } = this.config;
    const count = Math.round(baseCount * this._swell);
    const speed = baseSpeed * (0.8 + 0.2 * this._swell);
    const base = Math.random() * Math.PI * 2;
    for (let i = 0; i < count; i++) {
      const angle = base + (i / count) * Math.PI * 2;
      pool.fire({
        x: boss.x,
        y: boss.y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        radius: 2.6,
        damage,
        color: '#57e0d0',
        lifetime: 5,
      });
    }
  }

  getVisual(boss) {
    if (this.state !== PatternState.TELEGRAPH) return null;
    // Preview grows with the swell so a loud player sees a bigger wall coming.
    return {
      kind: 'swellRing',
      x: boss.x,
      y: boss.y,
      radius: 26 + this._swell * 34,
      t: 1 - Math.max(0, this._timer) / this.telegraphDuration,
    };
  }
}
